import type { ResumeData, TemplateType } from '../types/resume';
import { User, Briefcase, GraduationCap, Wrench, Folder, Award, Trash2, Plus } from 'lucide-react';
import { AiWriteButton } from './AiWriteButton';
import { AddSkillsWithAI } from './AddSkillsWithAI';

interface EditorProps {
  data: ResumeData;
  template: TemplateType;
  activeSection: string;
  setActiveSection: (s: string) => void;
  updatePersonal: (field: keyof ResumeData['personal'], value: string) => void;
  addExperience: () => void;
  updateExperience: (id: string, field: keyof ResumeData['experience'][0], value: any) => void;
  removeExperience: (id: string) => void;
  addEducation: () => void;
  updateEducation: (id: string, field: keyof ResumeData['education'][0], value: any) => void;
  removeEducation: (id: string) => void;
  addSkill: () => void;
  addSkills: (names: string[]) => void;
  updateSkill: (id: string, field: keyof ResumeData['skills'][0], value: any) => void;
  removeSkill: (id: string) => void;
  addProject: () => void;
  updateProject: (id: string, field: keyof ResumeData['projects'][0], value: any) => void;
  removeProject: (id: string) => void;
  addCertification: () => void;
  updateCertification: (id: string, field: keyof ResumeData['certifications'][0], value: any) => void;
  removeCertification: (id: string) => void;
}

const sections = [
  { key: 'personal', label: 'Personal Info', icon: User },
  { key: 'experience', label: 'Work Experience', icon: Briefcase },
  { key: 'education', label: 'Education', icon: GraduationCap },
  { key: 'skills', label: 'Skills', icon: Wrench },
  { key: 'projects', label: 'Projects', icon: Folder },
  { key: 'certifications', label: 'Certifications', icon: Award },
];

export function Editor(props: EditorProps) {
  const { activeSection, setActiveSection } = props;

  return (
    // Column on narrow screens (section nav becomes a horizontal scroller),
    // row with a fixed icon rail from `sm` up.
    <div className="flex h-full w-full min-w-0 flex-col sm:flex-row">
      {/* Section nav */}
      <nav
        aria-label="Resume sections"
        className="bg-slate-50 border-b sm:border-b-0 sm:border-r flex flex-row sm:flex-col items-center gap-2 shrink-0 px-3 py-2 sm:px-0 sm:py-4 sm:w-14 overflow-x-auto sm:overflow-x-visible scrollbar-thin"
      >
        {sections.map((sec) => {
          const Icon = sec.icon;
          const active = activeSection === sec.key;
          return (
            <button
              key={sec.key}
              onClick={() => setActiveSection(sec.key)}
              aria-current={active ? 'true' : undefined}
              className={`flex items-center gap-2 p-2.5 rounded-lg transition-colors shrink-0 ${
                active
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              }`}
              title={sec.label}
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span className="text-xs font-medium sm:hidden">{sec.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Form Area */}
      <div className="flex-1 min-w-0 p-4 sm:p-6 space-y-6 overflow-y-auto">
        {activeSection === 'personal' && <PersonalSection {...props} />}
        {activeSection === 'experience' && <ExperienceSection {...props} />}
        {activeSection === 'education' && <EducationSection {...props} />}
        {activeSection === 'skills' && <SkillsSection {...props} />}
        {activeSection === 'projects' && <ProjectsSection {...props} />}
        {activeSection === 'certifications' && <CertificationsSection {...props} />}
      </div>
    </div>
  );
}

function PersonalSection({ data, updatePersonal }: EditorProps) {
  const p = data.personal;
  const field = (label: string, key: keyof ResumeData['personal'], type = 'text') => (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {type === 'textarea' ? (
        <textarea
          value={p[key]}
          onChange={(e) => updatePersonal(key, e.target.value)}
          rows={4}
          className="mt-1 block w-full rounded-lg border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
        />
      ) : (
        <input
          type={type}
          value={p[key]}
          onChange={(e) => updatePersonal(key, e.target.value)}
          className="mt-1 block w-full rounded-lg border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
        />
      )}
    </label>
  );

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-slate-900">Personal Information</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {field('Full Name', 'fullName')}
        {field('Email', 'email', 'email')}
        {field('Phone', 'phone', 'tel')}
        {field('Location', 'location')}
        {field('Website', 'website', 'url')}
        {field('LinkedIn', 'linkedin', 'url')}
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">Professional Summary</span>
          <AiWriteButton
            type="summary"
            resume={data}
            context={p.summary}
            label="Write with AI"
            onApply={(text) => updatePersonal('summary', text)}
          />
        </div>
        <textarea
          value={p.summary}
          onChange={(e) => updatePersonal('summary', e.target.value)}
          rows={4}
          className="block w-full rounded-lg border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
          placeholder="Brief overview of your background and career goals"
        />
      </div>
    </div>
  );
}

function ExperienceSection({ data, addExperience, updateExperience, removeExperience }: EditorProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Work Experience</h2>
        <button
          onClick={addExperience}
          className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      <div className="space-y-4">
        {data.experience.map((exp) => (
          <div key={exp.id} className="rounded-xl border bg-slate-50 p-4 space-y-3">
            <div className="flex items-start justify-between">
              <h3 className="text-sm font-medium text-slate-500">{exp.company || 'New Position'}</h3>
              <button onClick={() => removeExperience(exp.id)} className="text-slate-400 hover:text-red-500">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                placeholder="Company"
                value={exp.company}
                onChange={(e) => updateExperience(exp.id, 'company', e.target.value)}
                className="rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
              <input
                placeholder="Title"
                value={exp.title}
                onChange={(e) => updateExperience(exp.id, 'title', e.target.value)}
                className="rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
              <input
                placeholder="Location"
                value={exp.location}
                onChange={(e) => updateExperience(exp.id, 'location', e.target.value)}
                className="rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
              <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-2">
                <input
                  type="text"
                  placeholder="Start (e.g. 10/2018)"
                  value={exp.startDate}
                  onChange={(e) => updateExperience(exp.id, 'startDate', e.target.value)}
                  className="rounded-lg border-slate-200 px-3 py-2 text-sm w-full"
                />
                <span className="text-slate-400">→</span>
                <input
                  type="text"
                  placeholder="End (e.g. 12/2019)"
                  value={exp.endDate}
                  onChange={(e) => updateExperience(exp.id, 'endDate', e.target.value)}
                  className="rounded-lg border-slate-200 px-3 py-2 text-sm w-full"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={exp.current}
                onChange={(e) => updateExperience(exp.id, 'current', e.target.checked)}
                className="rounded border-slate-300"
              />
              Current position
            </label>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">Description</span>
                <AiWriteButton
                  type="experience"
                  resume={data}
                  context={`Job: ${exp.title} at ${exp.company}\nLocation: ${exp.location}\nDates: ${exp.startDate} - ${exp.current ? 'Present' : exp.endDate}\n\nCurrent description:\n${exp.description}`}
                  label="Write with AI"
                  onApply={(text) => updateExperience(exp.id, 'description', text)}
                />
              </div>
              <textarea
                placeholder="Description"
                value={exp.description}
                onChange={(e) => updateExperience(exp.id, 'description', e.target.value)}
                rows={6}
                className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EducationSection({ data, addEducation, updateEducation, removeEducation }: EditorProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Education</h2>
        <button
          onClick={addEducation}
          className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      <div className="space-y-4">
        {data.education.map((edu) => (
          <div key={edu.id} className="rounded-xl border bg-slate-50 p-4 space-y-3">
            <div className="flex items-start justify-between">
              <h3 className="text-sm font-medium text-slate-500">{edu.school || 'New School'}</h3>
              <button onClick={() => removeEducation(edu.id)} className="text-slate-400 hover:text-red-500">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                placeholder="School"
                value={edu.school}
                onChange={(e) => updateEducation(edu.id, 'school', e.target.value)}
                className="rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
              <input
                placeholder="Degree"
                value={edu.degree}
                onChange={(e) => updateEducation(edu.id, 'degree', e.target.value)}
                className="rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
              <input
                placeholder="Field of Study"
                value={edu.field}
                onChange={(e) => updateEducation(edu.id, 'field', e.target.value)}
                className="rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
              <input
                placeholder="Location"
                value={edu.location}
                onChange={(e) => updateEducation(edu.id, 'location', e.target.value)}
                className="rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
              <input
                type="date"
                value={edu.startDate}
                onChange={(e) => updateEducation(edu.id, 'startDate', e.target.value)}
                className="rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
              <input
                type="date"
                value={edu.endDate}
                onChange={(e) => updateEducation(edu.id, 'endDate', e.target.value)}
                className="rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
            </div>
            <textarea
              placeholder="Description / Honors"
              value={edu.description}
              onChange={(e) => updateEducation(edu.id, 'description', e.target.value)}
              rows={2}
              className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function SkillsSection({ data, addSkill, addSkills, updateSkill, removeSkill }: EditorProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Skills</h2>
        <button
          onClick={addSkill}
          className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      <AddSkillsWithAI resume={data} onAdd={addSkills} />
      <div className="space-y-2">
        {data.skills.map((skill) => (
          <div key={skill.id} className="flex items-center gap-3">
            <input
              placeholder="Skill name"
              value={skill.name}
              onChange={(e) => updateSkill(skill.id, 'name', e.target.value)}
              className="flex-1 rounded-lg border-slate-200 px-3 py-2 text-sm"
            />
            <button onClick={() => removeSkill(skill.id)} className="text-slate-400 hover:text-red-500">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectsSection({ data, addProject, updateProject, removeProject }: EditorProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Projects</h2>
        <button
          onClick={addProject}
          className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      <div className="space-y-4">
        {data.projects.map((proj) => (
          <div key={proj.id} className="rounded-xl border bg-slate-50 p-4 space-y-3">
            <div className="flex items-start justify-between">
              <h3 className="text-sm font-medium text-slate-500">{proj.name || 'New Project'}</h3>
              <button onClick={() => removeProject(proj.id)} className="text-slate-400 hover:text-red-500">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <input
              placeholder="Project Name"
              value={proj.name}
              onChange={(e) => updateProject(proj.id, 'name', e.target.value)}
              className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
            />
            <input
              placeholder="URL"
              value={proj.url}
              onChange={(e) => updateProject(proj.id, 'url', e.target.value)}
              className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
            />
            <input
              placeholder="Technologies (comma-separated)"
              value={proj.technologies.join(', ')}
              onChange={(e) => updateProject(proj.id, 'technologies', e.target.value.split(',').map((t) => t.trim()).filter(Boolean))}
              className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
            />
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">Description</span>
                <AiWriteButton
                  type="project"
                  resume={data}
                  context={`Project: ${proj.name}\nTechnologies: ${proj.technologies.join(', ')}\nURL: ${proj.url}\n\nCurrent description:\n${proj.description}`}
                  label="Write with AI"
                  onApply={(text) => updateProject(proj.id, 'description', text)}
                />
              </div>
              <textarea
                placeholder="Description"
                value={proj.description}
                onChange={(e) => updateProject(proj.id, 'description', e.target.value)}
                rows={2}
                className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CertificationsSection({ data, addCertification, updateCertification, removeCertification }: EditorProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Certifications</h2>
        <button
          onClick={addCertification}
          className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      <div className="space-y-3">
        {data.certifications.map((cert) => (
          <div key={cert.id} className="rounded-xl border bg-slate-50 p-4 space-y-3">
            <div className="flex items-start justify-between">
              <h3 className="text-sm font-medium text-slate-500">{cert.name || 'New Certification'}</h3>
              <button onClick={() => removeCertification(cert.id)} className="text-slate-400 hover:text-red-500">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                placeholder="Certification Name"
                value={cert.name}
                onChange={(e) => updateCertification(cert.id, 'name', e.target.value)}
                className="rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
              <input
                placeholder="Issuer"
                value={cert.issuer}
                onChange={(e) => updateCertification(cert.id, 'issuer', e.target.value)}
                className="rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
              <input
                type="text"
                placeholder="Date (e.g. 2023)"
                value={cert.date}
                onChange={(e) => updateCertification(cert.id, 'date', e.target.value)}
                className="rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
              <input
                placeholder="Credential URL"
                value={cert.url}
                onChange={(e) => updateCertification(cert.id, 'url', e.target.value)}
                className="rounded-lg border-slate-200 px-3 py-2 text-sm"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
